<template lang="pug">
	.creative-speech-streaming
		Popup(v-if="popupVisible", ref="popup", :popupVisible.sync="popupVisible", :width="960", :height="640", :title="$tCaption('Speech streaming')", :closeOnOutsideClick="false", classes="tracker-dark", 
			:showMaximize="true", :buttons="buttons")
			.flex-col.space-medium.creative-speech-streaming(:style="{ '--active-sender-color': selectedTrack && selectedTrack.color || '#999', '--fontSize': `${fontSize.value || 14}px` }")
				.message.flex-row.space(v-for="message in messages", :style="{ '--color': message.color || '#999' }")
					.flex-col
						.speech-button.clickable.hoverable.flex-row.align-center.center(v-if="message && message.speak", @click="togglePlay(message.speak)")
							fas(:icon="message.speak.isPlaying ? 'stop' : 'play'")
					.flex-col
						.flex-row.space.align-center.upper(v-if="message.speak")
							.sender.flex-row.space-small.align-center
								span {{ message.sender }}
								span(v-if="message.speak.chunksProcessed < 100") ({{message.speak.chunksProcessed}}%)
								fas(v-else, icon="square-check")
							Loader(v-if="message.speak.activatingVoice", :text="$tCaption('Preparing voice')")
						.flex-col
							template(v-if="message.speak && message.speak.wordBoundary")
								.text.flex-row.space-words.wrap 
									.word(v-for="word in message.speak.wordBoundary", :class="{ played: word.isPlayed }") {{ word.originalWord }}
							.text(v-else) {{ message.message }}
			template(slot="actions-top")
				.flex-row.space.align-start
					DxSelectBox.sender-selector(:data-source="availableAggTracks", valueExpr="trackNumber", displayExpr="name", :value.sync="selectedTrackNumber", stylingMode="filled", :width="120")
					.flex-col.flex-1.space
						DxTextBox.flex-1(:value.sync="activeMessage", :placeholder="$tCaption('Message')", stylingMode="filled", valueChangeEvent="input", @enterKey="sendMessage")
						.flex-row.flex-1.justify-start(v-if="false")
							DxCheckBox(:value.sync="postOnPunctuationMarks", :text="$tCaption('Send on puntuation marks')")
					DxButton(stylingMode="text", icon="fas fa-share", @click="sendMessage")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Close', '@actions')", @click="hidePopup")
			template(slot="actions-left")
				DxButton(:text="$tCaption('Clear messages', '@actions')", @click="clear")
				DxButton(:text="$tCaption('Stop', '@actions')", @click="stop", :disabled="!isPlaying")
				DxCheckBox(:value.sync="autoPlay.value", :text="$tCaption('Auto-play')")
			template(slot="title-before")
				VuMeter(ref="vumeter", orientation="horizontal", :isPlaying="isPlaying", :filters="{ stereoAnalyser: recorder.stereoAnalyser }", :compact="true", size="180px")
</template>

<script>
	import Loader from 'app-core/Loader.vue';	
	import DxButton from 'devextreme-vue/button';
	import Popup from 'app-core/Popup.vue';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxCheckBox from 'devextreme-vue/check-box';
	import speech from 'app-core/speech';
	import ssmlHelper from 'view/insertion/ssml-helper';
	import { AudioSystem } from 'view/audioTracker/audio-system';
	import { findVoice } from 'view/insertion/cognitive-arrays';
	import storage from 'storage';
	import VuMeter from 'view/audioTracker/AudioTrackerVUMeter.vue';
	import audioHelper from 'view/audioTracker/audio-helper';

	export default {
		translationCategory: 'creative.CreativeSpeechStreaming',
		name: 'creative-speech-streaming',
		components: {
			Loader, DxButton, Popup, DxTextBox, DxSelectBox, DxCheckBox, VuMeter
		},
		props: ['aggregatedTracks'],
		data() {
			return {
				popupVisible: false,
				activeMessage: null,
				selectedTrackNumber: null,
				messages: [],
				postOnPunctuationMarks: false,
				audioContext: new AudioContext(),
				audioSystem: new AudioSystem(),
				fontSize: storage.prop('speechStreaming_fontSize', void 0, 14),
				autoPlay: storage.prop('speechStreaming_autoPlay', void 0, true),
				recorder: {
					stereoAnalyser: null
				}
			};
		},
		computed: {
			selectedTrack() {
				return this.availableAggTracks.find(f => f.trackNumber === this.selectedTrackNumber);
			},
			isPlaying() {
				return this.messages.some(f => f.speak?.isPlaying);
			},
			buttons() {
				return [
					{
						icon: 'growfont',
						stylingMode: 'text',
						type: 'normal',
						text: '',
						onClick: () => this.setFontSize(1),
						disabled: !this.messages?.length,
						hint: this.$tCaption('Grow font')
					},
					{
						icon: 'shrinkfont',
						stylingMode: 'text',
						type: 'normal',
						text: '',
						onClick: () => this.setFontSize(-1),
						disabled: !this.messages?.length,
						hint: this.$tCaption('Shrink font')
					}
				];
			},
			availableAggTracks() {
				return this.aggregatedTracks.filter(f => f.__tracks.some(t => t.type === 'textToSpeech' && t.insertion?.TtsProviderType !== 2));
			}
		},
		watch: {
			activeMessage(e) {
				if (this.postOnPunctuationMarks && `${e}`.match(/(.*[!.?,;])/g)) {
					this.sendMessage();
				}
			}
		},
		mounted() {
			if (__DEBUG__)
				window.creativeSpeechStreaming = this;
			document.addEventListener('keyup', this.onKeyUp);
			this.selectedTrackNumber = this.availableAggTracks[0]?.trackNumber;
		},
		destroyed() {
			document.removeEventListener('keyup', this.onKeyUp);
			delete this.$effects;
			this.stop();
		},
		methods: {
			setFontSize(coef) {
				this.fontSize.value = Math.max(7, this.fontSize.value * (coef > 0 ? 1.25 : (1 / 1.25)));
			},
			hidePopup() {
				this.popupVisible = false;
				this.stop();
			},
			show() {
				this.popupVisible = false;
				this.$nextTick(() => {
					this.popupVisible = true;
					this.navigateLast();
				});
			},
			async sendMessage() {
				let message = `${this.activeMessage || ''}`.trim();

				if (!message) return;
				
				let msg = {
					message,
					track: this.selectedTrack,
					sender: this.selectedTrack?.name,
					color: this.selectedTrack?.color
				}
				this.messages.push(msg);

				this.$nextTick(() => {
					this.activeMessage = '';
					this.navigateLast();
				});

				this.$set(msg, 'speak', await this.speak(message));
			},
			onKeyUp(e) {
				if (e.key === 'ArrowUp' && e.ctrlKey) {
					this.changeActiveTrack(-1);
					e.stopPropagation();
					e.preventDefault();
				}

				if (e.key === 'ArrowDown' && e.ctrlKey) {
					this.changeActiveTrack(1);
					e.stopPropagation();
					e.preventDefault();
				}
			},
			changeActiveTrack(dir) {
				let currentIdx = this.availableAggTracks.findIndex(f => f.trackNumber === this.selectedTrackNumber);
				currentIdx = Math.max(0, Math.min(this.availableAggTracks.length-1, currentIdx + dir));

				if (__DEBUG__)
					console.debug('Change active track', { dir, currentIdx });

				this.selectedTrackNumber = this.availableAggTracks[currentIdx]?.trackNumber;
			},
			navigateLast() {
				this.$nextTick(() => {
					let scrollView = this.$refs.popup?.$refs?.popupBody?.$refs.scrollView;
					if (scrollView) {
						scrollView.instance.scrollBy(9999);
					}
				});
			},
			clear() {
				this.stop();
				this.messages = [];
				this.navigateLast();
				this.$texts = [];
			},
			stop() {
				(this.messages || []).map(f => {
					try {
						if (f.data && f.isPlaying) {
							f.speak.isPlayed = true;
						}
						f.speak.isPlaying = false;
						f.speak.source.stop();
					} catch {
						//
					}
				});
			},
			togglePlay(speak) {
				let isPlaying = speak.isPlaying;
				this.stop();

				if (!isPlaying) {
					this.playSpeak(speak, true);
					this.processChunks(speak);
					if (this.autoPlay.value) {
						let cIdx = this.messages.findIndex(f => f.speak === speak);
						if (cIdx > -1) {
							this.$texts = this.messages.slice(cIdx, this.messages.length).map(f => {
								f.speak.isPlayed = false;
								return f.speak;
							});
						}
						if (__DEBUG__)
							console.debug('toggle', { cIdx, texts: this.$texts });
					}
				}
			},
			appendBuffer(buffer1, buffer2) {
				var tmp = new Uint8Array(buffer1.byteLength + buffer2.byteLength);
				var buff1 = new Uint8Array(buffer1);
				var buff2 = new Uint8Array(buffer2);

				tmp.set(buff1, 0);
				tmp.set(buff2, buffer1.byteLength);
				return tmp.buffer;
			},
			async processChunks(forcedSpeak) {
				let speak = forcedSpeak;
				if (!speak) {
					if (!this.autoPlay.value) {
						speak = (this.$texts || []).find(f => !f.activatingVoice && f.chunks.length);
					} else {
						speak = (this.$texts || []).find(f => !f.isPlayed);
					}
				}

				if (speak?.isProcessingChunk) return;

				if (__DEBUG__)
					console.debug('Process chunks', { speak, forcedSpeak });

				if (!speak || speak.activatingVoice) return;

				if (speak.chunks.length === 0 && speak.allChunks && speak.isPlayed) {
					let speakIdx = (this.$texts || []).findIndex(f => f === speak);
					if (speakIdx > -1) {
						this.$texts.splice(speakIdx, 1);
						this.processChunks();
					}
					return;
				}

				if (!speak.chunks.length) {
					if (this.autoPlay.value && !speak.isPlayed && !this.isPlaying) {
						this.playSpeak(speak, true);
					}
					return;
				}

				speak.isProcessingChunk = true;
				try {
					let audioData = speak.chunks[0];

					if (!speak.data) {
						speak.data = audioData;
						speak.lastDuration = 0;
						speak.scheduledTime = 0;
					} else {
						speak.data = this.appendBuffer(speak.data, audioData);
						speak.scheduledTime = 0.015;
					}

					let buffer = await this.audioContext.decodeAudioData(speak.data.slice(0));
					speak.buffer = buffer;

					if (!this.autoPlay.value && !speak.isPlaying) {
						this.processChunks(speak);
					} else {
						if (!speak.isPlayed && (speak.isPlaying || !this.isPlaying)) {
							this.playSpeak(speak);
						}
					}
				} finally {
					speak.chunks.splice(0, 1);
					speak.calcChunksProcessed();
					speak.isProcessingChunk = false;
					this.processChunks(forcedSpeak);
				}
			},
			playSpeak(speak, fromStart) {
				let currentTime = this.audioContext.currentTime;
				let source = this.audioContext.createBufferSource();
				let scheduledTime = speak.scheduledTime || 0;

				try {
					if (speak.source) {
						speak.source.stop(currentTime + scheduledTime);
					}
				} catch (e) {
					//
				}

				speak.isPlaying = true;

				if (__DEBUG__)
					console.debug('PlaySpeak', { data: speak.data });

				if (!speak.data) return;

				speak.source = source;
				if (!speak.started || fromStart) {
					speak.started = this.audioContext.currentTime;
					speak.wordBoundary.map(f => this.$set(f, 'isPlayed', false));
				}

				let checkPlayedState = null;
				checkPlayedState = setInterval(() => {
					let playedTotal = speak.playedTotal();
			
					speak.wordBoundary.filter(f => !f.isPlayed && (f.audioOffset / 10000000 <= playedTotal)).map(f => {
						this.$set(f, 'isPlayed', true);
					});

					if (this.__isDestroyed || !speak.isPlaying) {
						clearInterval(checkPlayedState);
						return;
					}
				}, 200);
					
				source.buffer = speak.buffer;

				var stereoAnalyser = new audioHelper.StereoAnalyserNode(this.audioContext);
				stereoAnalyser.fftSize = 512;
				this.recorder.stereoAnalyser = stereoAnalyser;

				source.connect(speak.effects.input);
				speak.effects.output.connect(stereoAnalyser);
				speak.effects.output.connect(this.audioContext.destination);

				source.addEventListener('ended', () => {
					if (source === speak.source && speak.allChunks && speak.chunks.length === 0) {
						if (__DEBUG__)
							console.debug('Played');
						speak.isPlayed = true;
						speak.isPlaying = false;
						delete speak.source;
						this.$nextTick(() => this.processChunks());
					}
				});

				let start = currentTime + scheduledTime;
				let offset = Math.max(0, Math.min(source.buffer?.duration || 0, this.audioContext.currentTime - (speak?.started || this.audioContext.currentTime)) + scheduledTime);
				speak.source.start(start, offset);
			},
			getActiveTrackInfo() {
				let voice = null;
				let language = null;
				let ssml = null;

				let activeTrack = this.selectedTrack?.__tracks.find(f => f.type === 'textToSpeech' && f.insertion?.TtsProviderType !== 2);
				if (activeTrack) {
					voice = activeTrack.insertion?.Voice;
					language = activeTrack.insertion?.Language;
					ssml = activeTrack.insertion?.Ssml;
				}
				
				return {
					voice: voice || 'en-US-JennyNeural',
					language: language || 'en-US',
					ssml
				}
			},
			prepareSsml(text) {
				let activeTrackInfo = this.getActiveTrackInfo();

				let templateSsml = activeTrackInfo.ssml || '';

				let leadingsilence = activeTrackInfo.voice.indexOf('OverDub') == -1 ? ' leadingsilence-exact="0ms"' : '';
				let tailingsilence = activeTrackInfo.voice.indexOf('OverDub') == -1 ? ' tailingsilence-exact="0ms"' : '';

				const valuableTags = [...(templateSsml.match(/<lexicon(.*?)lexicon>|<mstts\:customtn(.*?)customtn>/g) || [])].join('');
				let ssml = `<speak xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="http://www.w3.org/2001/mstts" xmlns:emo="http://www.w3.org/2009/10/emotionml" version="1.0" xml:lang="${activeTrackInfo.language}">
					<voice name="${activeTrackInfo.voice}"${leadingsilence}${tailingsilence}>
						${valuableTags}
						<prosody rate="1" pitch="+0%">
							<lang xml:lang="${activeTrackInfo.language}">${text}</lang>
						</prosody>
					</voice>
				</speak>`;

				let preparedSsml = ssmlHelper.prepareSsml(ssml, { pitch: 1, rate: 1, ...activeTrackInfo });

				if (__DEBUG__)
					console.log('Text to speech', { preparedSsml });

				return preparedSsml?.ssml;
			},
			async speak(text) {
				let activeTrackInfo = this.getActiveTrackInfo();
				let voice = findVoice(activeTrackInfo.voice);

				if (voice.isCustom) {
					this.$notifyWarning(this.$tCaption('Streaming not supported'));
					return;
				}

				if (__DEBUG__)
					console.log(`%c${text}`,'background: orange; color: white');

				if (!this.$lastContextPosition) {
					this.$lastContextPosition = 0;
				}

				if (!this.$effects) {
					this.$effects = {};
				}

				let effects = this.$effects[this.selectedTrack.trackNumber];
				if (!effects) {
					effects = await this.audioSystem.getPipelineEffects(this.selectedTrack, this.audioContext);
					this.$effects[this.selectedTrack.trackNumber] = effects;
					await this.audioSystem.restoreTrackEffects(this.selectedTrack, effects.filters);
				}

				let speak = {
					allChunks: false,
					chunks: [],
					effects: effects,
					wordBoundary: [],
					playedTotal: () => Math.min(speak.buffer?.duration || 0, this.audioContext.currentTime - (speak?.started || this.audioContext.currentTime)),
					isPlayed: false,
					activatingVoice: false,
					isPlaying: false,
					isProcessingChunk: false,
					totalChunks: 0,
					chunksProcessed: 0,
					calcChunksProcessed: () => {
						speak.chunksProcessed = parseInt(100 * (speak.totalChunks === 0 ? 0 : (1 - speak.chunks.length / speak.totalChunks)));
					}
				}

				if (!this.$texts) {
					this.$texts = [];
				}

				this.$texts.push(speak);

				const synthesizing = (e) => {
					if (__DEBUG__)
						console.debug(`On synthesizing ${this.$lastContextPosition}`);

					let audioData = e?.result?.audioData;

					if (!audioData?.byteLength) return;

					speak.chunks.push(audioData);
					speak.totalChunks++;
					this.processChunks(speak);
					speak.calcChunksProcessed();
				}

				let ssml = this.prepareSsml(text);

				speak.originalWords = `${text || ''}`
					.replace(/\./g, '. ')
					.replace(/\,/g, ', ')
					.replace(/\!/g, '! ')
					.replace(/\?/g, '? ')
					.split(' ').filter(f => f);

				let wordBoundary = (e) => {
					if (__DEBUG__)
						console.debug('Word boundary', { e });

					e.originalWord = speak.originalWords[speak.wordBoundary.length];
					speak.wordBoundary.push(e);
				}

				
				speak.voice = voice;

				speak.activatingVoice = true;
				speak.voice.__activate().then(async () => {
					speak.activatingVoice = false;
					await speech.textToSpeech(ssml, { synthesizing, isSsml: true, wordBoundary });	

					speak.allChunks = true;
					this.processChunks(speak);
				});

				return speak;
			},
		}
	};
</script>
<style lang='scss'>
	.creative-speech-streaming {
		.message {
			.sender {
				color: var(--color);
				font-size: calc(var(--fontSize) / 1.5);
			}

			.text {
				color: #777;
				font-size: var(--fontSize);

				.word.played {
					color: #fff;
				}
			}

			.space-words.flex-row > *:not(:last-child) {
				margin-right: 4px;
			}
		}

		.sender-selector {
			input {
				color: var(--active-sender-color) !important;
			}
		}

		.speech-button {
			width: 28px;
			height: 28px;
			background: rgba(0,0,0,0.075);
			border-radius: 4px;
			margin-top: 4px;
			user-select: none;
		}
	}
</style>
